import React, { useState, useCallback, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  createFilterOptions,
  Typography,
} from '@mui/material';
import { incidentsApi } from '../../../../functions/IncidentsApi';
import { debounce } from 'lodash';
import OutlinedInput from '@mui/material/OutlinedInput';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { getContrastColor } from '../../../../utils/getContrastColor';
import useIsVisible from '../../../../hooks/useIsVisible';

const getLabel = (option) => {
  const number = option?.number ? `${option.number}: ` : '';
  const label = option?.label || option;
  return `${number}${label}`;
};

const findMissingOptions = (options, optionsCache) => {
  const valuesSet = new Set(optionsCache.map((item) => item.value));
  return options.filter((item) => !valuesSet.has(item.value));
};

const IncidentAutocomplete = ({
  id,
  value,
  onChange,
  multiple = false,
  disabled = false,
  size = 'small',
  placeholder = 'Start searching...',
  sx = {},
}) => {
  const [selectedOptionsCache, setSelectedOptionsCache] = useState([]);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const { isVisible, bindingRefCallback } = useIsVisible({
    finishWhenVisible: true,
  });

  const fetchOptions = useCallback(
    debounce(async (searchValue = '') => {
      // fetch only when a user sees this selector
      if (!isVisible) {
        return;
      }

      setLoading(true);
      try {
        const response = (
          await incidentsApi.getIncidentTypeOptions(searchValue)
        ).data;

        const flattenedOptions = [];
        if (response && Array.isArray(response)) {
          response.forEach((group) => {
            if (group.types && Array.isArray(group.types)) {
              group.types.forEach((type) => {
                flattenedOptions.push({
                  ...type,
                  groupName: group.name,
                  groupColor: group.color,
                  value: type.option,
                });
              });
            }
          });
        }

        setOptions(flattenedOptions);
        setSelectedOptionsCache((prevState) => [
          ...prevState,
          ...findMissingOptions(flattenedOptions, prevState),
        ]);
      } catch (error) {
        console.error('Error fetching options:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [isVisible]
  );

  // init selector options
  useEffect(() => {
    fetchOptions('');
  }, [fetchOptions]);

  const findOptionByValue = (val) => {
    return selectedOptionsCache.find((option) => option?.value === val);
  };

  return (
    <Autocomplete
      ref={bindingRefCallback}
      id={id}
      multiple={multiple}
      options={options}
      value={value}
      onChange={(_, newValue) => {
        if (Array.isArray(newValue)) {
          onChange(newValue.map((item) => item?.value || item));
        } else {
          onChange(newValue?.value || newValue);
        }
      }}
      onInputChange={(event, newInputValue) => {
        // only users change allow
        if (!event) {
          return;
        }
        fetchOptions(newInputValue);
      }}
      loading={loading}
      disabled={disabled}
      size={size}
      sx={sx}
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return findOptionByValue(option)?.label || option;
        }
        return option?.label ?? '';
      }}
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) {
          return false;
        }

        const optionObj =
          typeof option === 'string' ? { value: option } : option;
        const valueObj = typeof value === 'string' ? { value } : value;

        return (
          optionObj.value === valueObj.value ||
          optionObj.label === valueObj.label ||
          optionObj.number === valueObj.number
        );
      }}
      filterOptions={createFilterOptions({
        stringify: (option) => {
          return Object.values(option).join(' ').toLowerCase();
        },
      })}
      groupBy={(option) => option.groupName}
      renderGroup={(params) => {
        const backgroundColor =
          options.find((opt) => opt.groupName === params.group)?.groupColor ||
          '#f5f5f5';

        return (
          <li key={params.key}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 16px',
                backgroundColor,
                color: getContrastColor(backgroundColor),
                fontWeight: 'bold',
                fontSize: '0.875rem',
              }}
            >
              {params.group}
            </Box>
            <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
          </li>
        );
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ padding: '8px 16px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: option.groupColor,
                marginRight: 1,
                flexShrink: 0,
              }}
            />

            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {getLabel(option)}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => {
        return (
          <OutlinedInput
            {...params.InputProps}
            inputProps={params.inputProps}
            placeholder={placeholder}
            size={size}
            fullWidth
            variant="outlined"
            endAdornment={
              <>
                {loading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            }
          />
        );
      }}
      renderTags={(value, getTagProps) =>
        value.map((item, index) => {
          const option = findOptionByValue(item) || item;
          return (
            <Chip
              key={option?.value || option?.option || index}
              label={getLabel(option)}
              size="small"
              sx={{
                backgroundColor: option?.groupColor || 'inherit',
                color: getContrastColor(option?.groupColor),
              }}
              deleteIcon={
                <HighlightOffIcon
                  sx={{
                    fill: getContrastColor(option?.groupColor),
                  }}
                />
              }
              {...getTagProps({ index })}
            />
          );
        })
      }
    />
  );
};

export default IncidentAutocomplete;
